import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from './Button'

export type ModalFooterSetter = (node: ReactNode | null) => void

const ModalFooterSetterContext = createContext<ModalFooterSetter | null>(null)

/** When rendering form content inside Modal, register primary actions here so they stay in the fixed footer. */
export function useModalFooterSlot(): ModalFooterSetter | null {
  return useContext(ModalFooterSetterContext)
}

export interface ModalProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  className?: string
  /**
   * Fixed footer below scrollable content. If set (including null), overrides slot registration.
   * Omit to allow children to register footer via useModalFooterSlot().
   */
  footer?: ReactNode | null
}

/**
 * Modal with backdrop, scrollable body, and optional fixed footer. Renders via portal.
 */
export const Modal = ({
  open,
  onClose,
  title,
  children,
  className,
  footer: footerProp,
}: ModalProps): ReactElement | null => {
  const [slotFooter, setSlotFooter] = useState<ReactNode>(null)

  const registerFooter = useCallback((node: ReactNode | null) => {
    setSlotFooter(node)
  }, [])

  useEffect(() => {
    if (!open) setSlotFooter(null)
  }, [open])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, onClose])

  if (!open) return null

  const resolvedFooter = footerProp !== undefined ? footerProp : slotFooter
  const showFooter = resolvedFooter != null && resolvedFooter !== false

  const content = (
    <div
      className="fixed inset-0 z-50 flex min-h-0 items-start justify-center overflow-y-auto overflow-x-hidden p-4 pointer-events-none sm:items-center sm:py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className="pointer-events-auto fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Enter' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close modal"
      />
      <div
        className={cn(
          'pointer-events-auto relative z-10 my-auto grid w-full max-w-md max-h-[min(96dvh,calc(100dvh-1rem))] grid-rows-[auto_minmax(0,1fr)_auto] overflow-hidden rounded-lg bg-white shadow-xl dark:bg-gray-800',
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative z-10 flex min-h-0 shrink-0 items-center justify-between gap-2 border-b border-gray-100 px-5 py-4 dark:border-gray-700">
          {title && (
            <h2 id="modal-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
          )}
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            className="ml-auto -mr-2 shrink-0 p-2"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <ModalFooterSetterContext.Provider value={registerFooter}>
          <div
            className="relative z-0 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-5 py-5 [-webkit-overflow-scrolling:touch]"
            tabIndex={-1}
          >
            {children}
          </div>
          {showFooter && (
            <div
              className="relative z-20 flex min-h-0 shrink-0 flex-wrap items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-4 dark:border-gray-700 dark:bg-gray-900/40"
              data-modal-footer
            >
              {resolvedFooter}
            </div>
          )}
        </ModalFooterSetterContext.Provider>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
